import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/client';
import { NotificationChannelInput } from '../types';

export function useNotificationTypes() {
  return useQuery({
    queryKey: ['notification-types'],
    queryFn: api.getNotificationTypes,
    staleTime: Infinity,
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: api.getNotifications,
  });
}

export function useCreateNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: NotificationChannelInput) => api.createNotification(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notifications'] }); },
  });
}

export function useUpdateNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: NotificationChannelInput }) =>
      api.updateNotification(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notifications'] }); },
  });
}

export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteNotification(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notifications'] }); },
  });
}

export function useTestNotification() {
  return useMutation({
    mutationFn: ({ id, event }: { id: number; event?: string }) => api.testNotification(id, event),
  });
}

export function useTestNotificationConfig() {
  return useMutation({
    mutationFn: (data: Parameters<typeof api.testNotificationConfig>[0]) => api.testNotificationConfig(data),
  });
}

export function useNotificationDeliveries(id: number) {
  return useQuery({
    queryKey: ['notification-deliveries', id],
    queryFn: () => api.getNotificationDeliveries(id),
    enabled: id > 0,
  });
}

export function useRedeliverNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, deliveryId }: { id: number; deliveryId: number }) => api.redeliverNotification(id, deliveryId),
    onSuccess: (_, vars) => { qc.invalidateQueries({ queryKey: ['notification-deliveries', vars.id] }); },
  });
}
